import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { chapters } from "../data/chapters";
import { CheckCircle2, Circle, ArrowLeft, Printer } from "lucide-react";
import axios from "axios";
import { log } from "../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrintableChecklist() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/progress`, { withCredentials: true });
        if (data.completed_steps) setCompletedSteps(data.completed_steps);
      } catch (err) {
        log.warn("Failed to load progress", err);
      }
      setReady(true);
    };
    load();
  }, [user]);

  const handlePrint = () => window.print();

  if (loading || !user || !ready) return null;

  const chapterChecklistStart = chapters.map((_, i) =>
    chapters.slice(0, i).reduce((sum, c) => sum + c.checklistItems.length, 0)
  );

  const totalChecklist = chapters.reduce((a, c) => a + c.checklistItems.length, 0);
  const completedTotal = completedSteps.length;
  const progressPercent = totalChecklist > 0 ? Math.round((completedTotal / totalChecklist) * 100) : 0;

  return (
    <div data-testid="printable-checklist" className="min-h-screen bg-[var(--c-bg)]">
      {/* Screen-only header */}
      <div className="print-hide sticky top-0 z-10 border-b-2 border-[var(--c-border)] bg-[var(--c-bg-95)] backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <button
            data-testid="back-to-ebook"
            onClick={() => navigate("/ebook")}
            className="flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2 text-sm font-bold shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to eBook
          </button>
          <button
            data-testid="print-button"
            onClick={handlePrint}
            className="flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-5 py-2 text-sm font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
          >
            <Printer className="h-4 w-4" />
            Print Checklist
          </button>
        </div>
      </div>

      {/* Printable content */}
      <div className="mx-auto max-w-3xl px-6 py-8">
        {/* Title */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 inline-flex h-24 items-center justify-center overflow-hidden rounded-2xl border-2 border-[var(--c-border)] bg-white px-5 shadow-[4px_4px_0px_var(--c-shadow)]">
            <img src="/logo.png" alt="Texas LLC Guide" className="h-20 w-auto object-contain" data-testid="checklist-logo" />
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-[var(--c-text)] sm:text-4xl">
            Texas LLC Formation Checklist
          </h1>
          <p className="mt-2 text-[var(--c-muted)]">
            {user?.name ? `Prepared for ${user.name}` : ""} &middot; {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
          </p>
          <div className="mt-4 inline-block rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-5 py-2 text-sm font-bold shadow-[3px_3px_0px_var(--c-shadow)]">
            {progressPercent}% Complete &middot; {completedTotal} of {totalChecklist} tasks
          </div>
        </div>

        {/* Chapters */}
        <div className="space-y-6">
          {chapters.map((ch, i) => {
            const startIdx = chapterChecklistStart[i];
            const chCompleted = ch.checklistItems.filter(
              (_, j) => completedSteps.includes(startIdx + j)
            ).length;
            const chTotal = ch.checklistItems.length;
            const allDone = chCompleted === chTotal;

            return (
              <div
                key={ch.id}
                data-testid={`print-chapter-${ch.id}`}
                className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-6 shadow-[4px_4px_0px_var(--c-shadow)] print-no-break"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-lg border-2 text-sm font-bold ${
                        allDone
                          ? "border-[var(--c-border)] bg-[#A3E6D5] text-[var(--c-text)]"
                          : "border-[var(--c-border)] bg-[#1E3A8A] text-white"
                      }`}
                    >
                      {allDone ? <CheckCircle2 className="h-4 w-4" /> : ch.id}
                    </div>
                    <h2 className="font-heading text-lg font-bold text-[var(--c-text)]">
                      {ch.title}
                    </h2>
                  </div>
                  <span className="text-xs font-bold text-[var(--c-muted)]">
                    {chCompleted}/{chTotal}
                  </span>
                </div>

                <div className="space-y-2">
                  {ch.checklistItems.map((item, ci) => {
                    const idx = startIdx + ci;
                    const checked = completedSteps.includes(idx);
                    return (
                      <div
                        key={`${ch.id}-item-${ci}`}
                        data-testid={`print-item-${ch.id}-${ci}`}
                        className="flex items-start gap-3 rounded-lg border border-[var(--c-border-light)] p-3"
                      >
                        {checked ? (
                          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#A3E6D5]" />
                        ) : (
                          <Circle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--c-border)]" />
                        )}
                        <span
                          className={`text-sm font-medium ${
                            checked
                              ? "text-[var(--c-muted)] line-through"
                              : "text-[var(--c-text)]"
                          }`}
                        >
                          {item}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-[var(--c-muted)]">
          Texas LLC Formation Guide &middot; Generated from your saved progress
        </div>
      </div>
    </div>
  );
}
